import { FCElement } from '@fc/component';

/**
 * Newsletter Popup — handles trigger logic and suppression.
 *
 * Reads trigger configuration from data attributes set in
 * `sections/newsletter-popup.liquid` and opens the nested
 * `<overlay-dialog>` after the configured event fires.
 *
 * Suppression is stored per section and respects the
 * `data-frequency` setting (once | session | sessions | day | week).
 *
 * `sessions` shows the popup every N sessions of the same visitor
 * (N = `data-session-interval`): 1st, N+1th, 2N+1th... session.
 * Visitors who subscribe are never shown the popup again.
 *
 * Storage is best-effort only. If the browser blocks access,
 * the popup still opens and closes normally.
 */

const STORAGE_NAMESPACE = 'newsletter_popup';

const FREQ_MS = {
  once: Infinity,
  session: 0, // handled via sessionStorage flag
  sessions: 0, // handled via session counter (every N sessions)
  day: 86_400_000,
  week: 604_800_000,
};

const VALID_FREQUENCIES = new Set(Object.keys(FREQ_MS));

class NewsletterPopupComponent extends FCElement {
  /** @type {AbortController|null} */
  #ac = null;
  #formState = null;
  #showRetry = null;

  connectedCallback() {
    super.connectedCallback();

    if (Shopify.designMode) {
      this.#setupEditorPreview();
      return;
    }

    const statusMessage = this.#getStatusMessage();
    if (statusMessage) {
      this.#formState = statusMessage.state;

      if (this.#formState === 'success') {
        this.#recordDismissal({ permanent: true });
      }

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#show({
          reason: `form-${this.#formState}`,
          focusTarget: statusMessage.element,
          recordDismissalOnClose: this.#formState !== 'error',
        });
      });
      return;
    }

    if (this.#shouldSuppress()) return;

    this.#ac = new AbortController();
    this.#bindCloseButton();
    this.#setupTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#showRetry != null) {
      clearTimeout(this.#showRetry);
      this.#showRetry = null;
    }
    this.#ac?.abort();
    this.#ac = null;
  }

  /* ── Editor preview ─────────────────────────────────── */

  #setupEditorPreview() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.addManagedEventListener(document, 'shopify:section:load', (e) => {
      if (e.detail.sectionId === sectionId) this.#showDialog();
    });

    this.addManagedEventListener(document, 'shopify:section:select', (e) => {
      if (e.detail.sectionId === sectionId) this.#showDialog();
    });

    this.addManagedEventListener(document, 'shopify:section:deselect', (e) => {
      if (e.detail.sectionId === sectionId) this.#closeDialog();
    });
  }

  #getDialogComponent() {
    const dialog = this.refs.dialogComponent || this.querySelector('overlay-dialog');
    return dialog instanceof HTMLElement ? dialog : null;
  }

  #showDialog(detail = {}) {
    const dialog = this.#getDialogComponent();
    if (dialog && typeof dialog.showDialog === 'function') dialog.showDialog(detail);
  }

  #closeDialog() {
    const dialog = this.#getDialogComponent();
    if (dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
  }

  #getStatusMessage() {
    const success = this.querySelector('.email-signup__message--success:not(.email-signup__message--hidden)');
    if (success instanceof HTMLElement) {
      return { state: 'success', element: success };
    }

    const error = this.querySelector('.email-signup__message--error:not(.email-signup__message--hidden)');
    if (error instanceof HTMLElement) {
      return { state: 'error', element: error };
    }

    return null;
  }

  #bindCloseButton() {
    const closeButton = this.querySelector('.newsletter-popup__close');
    const signal = this.#ac?.signal;
    if (!(closeButton instanceof HTMLButtonElement) || !signal) return;

    closeButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal },
    );

    closeButton.addEventListener(
      'keydown',
      (event) => {
        if (!['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        this.close();
      },
      { signal },
    );
  }

  /* ── Suppression ──────────────────────────────────────── */

  #getFrequency() {
    const frequency = this.dataset.frequency?.trim();
    return VALID_FREQUENCIES.has(frequency) ? frequency : 'once';
  }

  #getStorageKey() {
    const sectionId = this.dataset.sectionId?.trim();
    return sectionId ? `${STORAGE_NAMESPACE}:${sectionId}` : STORAGE_NAMESPACE;
  }

  #getStorageKeys() {
    const primaryKey = this.#getStorageKey();
    return primaryKey === STORAGE_NAMESPACE ? [primaryKey] : [primaryKey, STORAGE_NAMESPACE];
  }

  #migrateStorageValue(storage, sourceKey, value) {
    const targetKey = this.#getStorageKey();
    if (!storage || sourceKey === targetKey || value == null) return;

    try {
      storage.setItem(targetKey, value);
      storage.removeItem(sourceKey);
    } catch {
      // Storage is best-effort only.
    }
  }

  #readStorage(scope) {
    try {
      const storage = scope === 'session' ? window.sessionStorage : window.localStorage;
      for (const [index, key] of this.#getStorageKeys().entries()) {
        const value = storage?.getItem(key);
        if (value == null) continue;

        if (index > 0) {
          this.#migrateStorageValue(storage, key, value);
        }

        return value;
      }

      return null;
    } catch {
      return null;
    }
  }

  #writeStorage(scope, value) {
    try {
      const storage = scope === 'session' ? window.sessionStorage : window.localStorage;
      storage?.setItem(this.#getStorageKey(), value);
    } catch {
      // Storage is best-effort only.
    }
  }

  #removeStorage(scope) {
    try {
      const storage = scope === 'session' ? window.sessionStorage : window.localStorage;
      for (const key of this.#getStorageKeys()) {
        storage?.removeItem(key);
      }
    } catch {
      // Storage is best-effort only.
    }
  }

  #readSuppressionTimestamp() {
    const raw = this.#readStorage('local');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      const timestamp = Number(parsed?.ts);

      if (!Number.isFinite(timestamp) || timestamp <= 0) {
        this.#removeStorage('local');
        return null;
      }

      return timestamp;
    } catch {
      this.#removeStorage('local');
      return null;
    }
  }

  /* ── Every-N-sessions logic ───────────────────────────── */

  #getSessionInterval() {
    const n = parseInt(this.dataset.sessionInterval, 10);
    return Number.isFinite(n) && n >= 1 ? n : 3;
  }

  #getSessionsKey() {
    return `${this.#getStorageKey()}:sessions`;
  }

  #shouldSuppressBySessions() {
    try {
      const key = this.#getSessionsKey();
      const local = window.localStorage;
      const session = window.sessionStorage;

      let state = {};
      try {
        state = JSON.parse(local.getItem(key)) || {};
      } catch {
        state = {};
      }

      // Already subscribed: never show again
      if (state.subscribed) return true;

      // Count the session only once per browser session
      if (session.getItem(`${key}:counted`) !== '1') {
        session.setItem(`${key}:counted`, '1');
        state.count = (Number(state.count) || 0) + 1;
        local.setItem(key, JSON.stringify(state));
      }

      // Already dismissed in this session: don't show it again on other pages
      if (session.getItem(`${key}:dismissed`) === '1') return true;

      // 1st, 4th, 7th session... (with interval = 3).
      // To skip the very first visit use: Number(state.count) % interval !== 0
      return (Number(state.count) - 1) % this.#getSessionInterval() !== 0;
    } catch {
      return false; // storage blocked: best-effort behavior
    }
  }

  #shouldSuppress() {
    const freq = this.#getFrequency();

    // Every N sessions: local counter + session flags
    if (freq === 'sessions') {
      return this.#shouldSuppressBySessions();
    }

    // Session-based: check sessionStorage
    if (freq === 'session') {
      return this.#readStorage('session') === '1';
    }

    const timestamp = this.#readSuppressionTimestamp();
    if (!timestamp) return false;

    const maxAge = FREQ_MS[freq] ?? Infinity;
    if (!Number.isFinite(maxAge)) return true; // 'once' — never show again
    return Date.now() - timestamp < maxAge;
  }

  #recordDismissal({ permanent = false } = {}) {
    const freq = this.#getFrequency();

    if (freq === 'sessions') {
      try {
        const key = this.#getSessionsKey();
        window.sessionStorage.setItem(`${key}:dismissed`, '1');
        if (permanent) {
          let state = {};
          try {
            state = JSON.parse(window.localStorage.getItem(key)) || {};
          } catch {
            state = {};
          }
          window.localStorage.setItem(key, JSON.stringify({ ...state, subscribed: true }));
        }
      } catch {
        // Storage is best-effort only.
      }
      return;
    }

    if (freq === 'session') {
      this.#writeStorage('session', '1');
      return;
    }

    this.#writeStorage('local', JSON.stringify({ ts: Date.now() }));
  }

  /* ── Trigger setup ────────────────────────────────────── */

  #setupTrigger() {
    const type = this.dataset.trigger || 'delay';
    const signal = this.#ac?.signal;

    switch (type) {
      case 'delay': {
        const seconds = parseInt(this.dataset.delay, 10) || 5;
        const id = setTimeout(() => this.#show(), seconds * 1000);
        signal?.addEventListener('abort', () => clearTimeout(id));
        break;
      }

      case 'scroll': {
        const pct = (parseInt(this.dataset.scroll, 10) || 50) / 100;
        const handler = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0 || window.scrollY / scrollable >= pct) {
            this.#show();
            window.removeEventListener('scroll', handler);
          }
        };
        window.addEventListener('scroll', handler, { passive: true, signal });
        break;
      }

      case 'exit_intent': {
        const handler = (e) => {
          if (e.clientY <= 0) {
            this.#show();
            document.documentElement.removeEventListener('mouseleave', handler);
          }
        };
        document.documentElement.addEventListener('mouseleave', handler, { signal });
        break;
      }
    }
  }

  /* ── Show / close ─────────────────────────────────────── */

  #shown = false;

  #hasBlockingSurface() {
    const ownDialog = this.querySelector('dialog[open]');

    return [...document.querySelectorAll('dialog[open][scroll-lock], details[open][scroll-lock]')].some((surface) => {
      if (!(surface instanceof HTMLElement)) return false;
      if (surface === ownDialog) return false;
      return !this.contains(surface);
    });
  }

  #shouldDelayForActiveFocus() {
    const active = document.activeElement;

    if (!(active instanceof HTMLElement)) return false;
    if (active === document.body || active === document.documentElement) return false;
    if (this.contains(active)) return false;

    return true;
  }

  #show({ reason = 'trigger', focusTarget = null, recordDismissalOnClose = true } = {}) {
    if (this.#shown) return;

    if (this.#hasBlockingSurface() || (reason === 'trigger' && this.#shouldDelayForActiveFocus())) {
      if (this.#showRetry != null) {
        clearTimeout(this.#showRetry);
      }

      this.#showRetry = window.setTimeout(() => {
        this.#showRetry = null;
        this.#show({ reason, focusTarget, recordDismissalOnClose });
      }, 500);
      return;
    }

    this.#shown = true;
    if (this.#showRetry != null) {
      clearTimeout(this.#showRetry);
      this.#showRetry = null;
    }

    const dialog = this.#getDialogComponent();
    if (dialog && typeof dialog.showDialog === 'function') {
      if (focusTarget instanceof HTMLElement) {
        dialog.addEventListener(
          'dialog:open',
          () => {
            requestAnimationFrame(() => {
              if (focusTarget.isConnected) {
                focusTarget.focus();
              }
            });
          },
          { once: true },
        );
      }

      dialog.showDialog({ reason });
      dialog.addEventListener(
        'dialog:close',
        () => {
          if (recordDismissalOnClose) {
            this.#recordDismissal();
          }
        },
        { once: true },
      );
    }
  }

  /**
   * Declarative close handler — bound via on:click="/close"
   * on the close button in the Liquid template.
   */
  close() {
    const dialog = this.#getDialogComponent();
    if (dialog && typeof dialog.closeDialog === 'function') {
      dialog.closeDialog();
    }
  }
}

const TAG = 'newsletter-popup';
if (!customElements.get(TAG)) {
  customElements.define(TAG, NewsletterPopupComponent);
}
